<template>
  <Layout>
    <PageHeader
      class="mc-page-three"
      :routeBack="'/setting/custom-field'"
      :title="title"
      :items="breadcrumbItems"
      hasBreadcrumb
    />
    <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
      <McButtonBack class="d-inline-block me-2" />
      <McButton
        text="Tambah"
        icon="icon-outline_plus"
        class="d-inline-block"
        :disabled="isLoading"
        @click="handleShowCreateModal"
      />
    </div>

    <div class="card">
      <div class="card-body">
        <b-row align-h="end">
          <b-col md="4" xl="2" class="mt-0 mb-4">
            <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
              <template #prepend>
                <i class="ps-2 mc-icon icon-fill_search"></i>
              </template>
              <b-form-input
                ref="searchInput"
                :placeholder="$t('global.search') + '...'"
                :value="search"
                :disabled="isLoading"
                @input="handleSearchChange"
              />
              <template v-if="search" #append>
                <button class="clear-search-btn" @click="handleClearSearch">
                  <i class="pe-2 mc-icon icon-outline_close"></i>
                </button>
              </template>
            </b-input-group>
          </b-col>
        </b-row>

        <TableCustomField
          :fields="deliveryIndexFields"
          :items="items"
          :isLoading="isLoading"
          @drag="handleChangeOrder"
          @click-edit="handleShowFormModal"
          @click-delete="handleShowDeleteModal"
        >
          <template #cell(type)="{ value }">
            {{ formatType(value) }}
          </template>

          <template #cell(is_required)="{ value }">
            {{ value ? 'Ya' : 'Tidak' }}
          </template>

          <template #cell(is_active)="{ value, item }">
            <McToggle
              :key="toggleKeys[item.id]"
              :value="value"
              @change="(event) => handleToggleActive({ item, value: event })"
            />
          </template>
        </TableCustomField>
      </div>
    </div>

    <ModalForm
      ref="modal-form"
      :formType="formType"
      :selectedItem="selectedItem"
      @reloadList="getData"
    />
    <ModalDelete ref="modal-delete" @submit="handleDelete" />
    <ModalToggleActive
      ref="modal-toggle-active"
      :isLoading="isLoading"
      :isToggleActive="isToggleActive"
      @submit="handleConfirmToggle"
      @update:toggleKey="handleUpdateToggleKey"
    />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { debounce } from '@/helpers/debounce'
import { computed } from 'vue'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McToggle from '@/components/McToggle'
import TableCustomField from '../components/TableCustomField'
import ModalForm from './components/ModalForm'
import ModalDelete from './components/ModalDelete'
import ModalToggleActive from './components/ModalToggleActive'
import McButtonBack from '@/components/McButtonBack'

import { deliveryIndexFields } from './lib/fields/delivery-index-fields.js'
import { typeItems } from './lib/constants/type-items.js'
import * as CustomField from '@/services/rest-api/lib/setting/custom-field'

export default {
  page: {
    title: 'Setting',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McToggle,
    TableCustomField,
    ModalForm,
    ModalDelete,
    ModalToggleActive,
    McButtonBack,
  },
  provide() {
    return {
      selectedItem: computed(() => this.selectedItem),
      typeItems: computed(() => this.typeItems),
    }
  },
  data() {
    return {
      deliveryIndexFields,
      typeItems,

      toggleKeys: {},
      selectedItem: {},
      items: [],

      search: '',
      formType: null,

      isLoading: true,
      isToggleActive: false,
    }
  },
  computed: {
    title() {
      return 'Pengiriman'
    },
    breadcrumbItems() {
      return [
        {
          text: this.$t('setting.list.development.setting'),
          to: '/setting',
        },
        {
          text: 'Kolom Custom',
          to: '/setting/custom-field',
        },
        {
          text: 'Pengiriman',
        },
      ]
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    async getData(opts = {}) {
      this.isLoading = true
      const res = await CustomField.get(opts)

      if (res.errors) {
        return
      }

      this.items = res.data
      res.data.forEach((item) => {
        this.$set(this.toggleKeys, item.id, 1)
      })
      this.isLoading = false
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        search: _search != '' ? _search : undefined,
      }

      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange(undefined)
    },
    formatType(value) {
      return typeItems.find((el) => el.key == value).title
    },
    handleShowCreateModal() {
      this.formType = 'create'
      this.$refs['modal-form'].showModal()
    },
    async handleChangeOrder(event) {
      this.isLoading = true
      const res = await CustomField.update(event.element.id, { order: event.newIndex + 1 })

      if (res.errors) {
        this.isLoading = false
      }

      await this.getData()
    },
    handleToggleActive({ item, value }) {
      this.isToggleActive = value
      this.selectedItem = item

      this.$refs['modal-toggle-active'].showModal()
    },
    async handleConfirmToggle() {
      this.isLoading = true
      const res = await CustomField.update(this.selectedItem.id, { is_active: this.isToggleActive })

      if (res.errors) {
        return
      }

      this.selectedItem = {}
      this.$refs['modal-toggle-active'].hideModal()
      this.$mcToast.success(`Berhasil ${this.isToggleActive ? 'diaktifkan' : 'dinonaktifkan'}.`)

      await this.getData()
    },
    handleUpdateToggleKey() {
      const id = this.selectedItem.id
      const currentKey = this.toggleKeys[id]

      this.$set(this.toggleKeys, id, currentKey + 1)
    },
    async handleShowFormModal({ item }) {
      this.formType = 'update'
      const res = await CustomField.show(item.id)

      if (res.errors) {
        return
      }

      this.selectedItem = res.data
      this.$refs['modal-form'].showModal('form')
    },
    handleShowDeleteModal({ item }) {
      this.selectedItem = item
      this.$refs['modal-delete'].showModal()
    },
    async handleDelete() {
      this.isLoading = true

      const res = await CustomField.deleteSingle(this.selectedItem.id)

      if (res.errors) {
        return
      }

      await this.getData()

      this.selectedItem = {}
      this.$mcToast.success('Berhasil dihapus.')
      this.$refs['modal-delete'].hideModal()

      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-has-search {
  border-width: $input-border-width;
  border-color: $input-border-color;
  border-style: solid;
  border-radius: 4px;

  // if search is disabled
  &:has(input:disabled) {
    background: $input-disabled-bg;
  }

  .clear-search-btn {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .clear-search-btn:disabled {
    cursor: default;
  }

  input {
    border: none;
    border-radius: 0;
  }

  .input-group-prepend,
  .input-group-append {
    border: none;
    display: flex;
    align-items: center;

    .mc-icon:before {
      font-size: 16px;
      color: $input-placeholder-color;
    }
  }
}
</style>
